import { useState } from "react";
import { SEO, breadcrumbLd } from "@/lib/seo";
import { PHOTO_GALLERY, GALLERY_CATEGORIES } from "@/lib/constants";
import Reveal, { SectionHead } from "@/components/Reveal";
import BeforeAfter from "@/components/BeforeAfter";

const Realizacje = () => {
  const [cat, setCat] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  const photos = PHOTO_GALLERY.filter((p) => cat === "all" || p.category === cat);

  return (
    <>
      <SEO
        title="Realizacje przed i po – nasze prace | AD Brikman Łęczna, Lublin"
        description="Galeria realizacji AD Brikman: podjazdy, tarasy, opaski, chodniki i place z kostki brukowej oraz płyt tarasowych. Zobacz nasze prace przed i po — Łęczna, Lublin, Świdnik i okolice."
        path="/realizacje"
        jsonLd={breadcrumbLd([{ label: "Strona główna", path: "/" }, { label: "Realizacje", path: "/realizacje" }])}
      />

      <section className="grain relative overflow-hidden bg-ink pt-36 pb-16 lg:pt-44">
        <div className="paver-bg absolute inset-0 opacity-60" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
          <Reveal>
            <p className="eyebrow mb-5">Portfolio</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display max-w-4xl text-5xl font-black uppercase leading-[0.95] tracking-tight text-pearl sm:text-7xl" data-testid="realizacje-title">
              Nasze <span className="text-amber">realizacje</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-stonemute">
              Zdjęcia z naszych budów — podjazdy, tarasy, opaski i nawierzchnie wykonane w Łęcznej, Lublinie, Świdniku i na całym Lubelszczyźnie.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 pt-12 sm:px-8" data-testid="realizacje-beforeafter">
        <SectionHead eyebrow="Przed i po" title="Zobacz różnicę" sub="Przesuń suwak, aby porównać teren przed rozpoczęciem prac i gotową nawierzchnię." />
        <Reveal>
          <BeforeAfter
            before="https://images.unsplash.com/photo-1652303713917-2666b8bee507?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            after="https://images.pexels.com/photos/8134846/pexels-photo-8134846.jpeg?auto=compress&cs=tinysrgb&w=1200"
            alt="Realizacja przed i po"
          />
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-24" data-testid="realizacje-gallery">
        <div className="mb-10 flex flex-wrap gap-3" data-testid="gallery-filters">
          {GALLERY_CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              data-testid={`filter-chip-${c.id}`}
              className={`notch-sm px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${cat === c.id ? "bg-amber text-ink" : "border border-white/10 bg-graph text-stonemute hover:text-pearl"}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
          {photos.map((p, i) => (
            <Reveal key={p.src} delay={(i % 3) * 0.07}>
              <button
                onClick={() => setLightbox(p.src)}
                data-testid={`gallery-item-${p.src.split("/").pop().replace(".jpg", "")}`}
                className="card-edge group relative block w-full overflow-hidden"
                aria-label={`Powiększ zdjęcie realizacji ${i + 1}`}
              >
                <img src={p.src} alt={`Realizacja AD Brikman — ${GALLERY_CATEGORIES.find((c) => c.id === p.category)?.label}`} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute bottom-3 right-3 bg-ink/70 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-amber">
                  {GALLERY_CATEGORIES.find((c) => c.id === p.category)?.label}
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/90 p-6 backdrop-blur-sm"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <img src={lightbox} alt="Realizacja AD Brikman — powiększenie" className="max-h-[85vh] max-w-full object-contain" data-testid="lightbox-image" />
          <button className="absolute right-6 top-6 font-mono text-xs uppercase tracking-[0.2em] text-pearl hover:text-amber" data-testid="lightbox-close">
            Zamknij ✕
          </button>
        </div>
      )}
    </>
  );
};

export default Realizacje;
